import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import ScoutingForm from './components/ScoutingForm';
import DataAnalysis from './components/DataAnalysis';
import ChatBot from './components/ChatBot';
import TestChat from './components/TestChat';
import AdminTools from './components/AdminTools';
import PendingSubmissionsBanner from './components/PendingSubmissionsBanner';
import './App.css';

// ScrollToTop component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsLoading(false);
      return;
    }
    fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'verify', token })
    })
      .then(res => res.json())
      .then(data => {
        if (data.valid) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('authToken');
        }
      })
      .catch(() => {
        localStorage.removeItem('authToken');
      })
      .finally(() => setIsLoading(false));
  }, []);
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      
      if (data.success && data.token) {
        localStorage.setItem('authToken', data.token);
        setIsAuthenticated(true);
      } else {
        setError('Incorrect password. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    }
  };
  
  // Password component
  const PasswordProtection = () => (
    <div className="password-protection">
      <div className="password-container">
        <h1 className="app-title">robowhales|9032</h1>
        <h2>Scouting App</h2>
        <p>Please enter the password to access the scouting app:</p>
        
        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="password-input"
              autoFocus
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button type="submit" className="submit-btn">
            Enter
          </button>
        </form>
      </div>
    </div>
  );
  
  const ProtectedRoute = ({ element }) => {
    if (isLoading) return <div className="container" style={{ textAlign: 'center', marginTop: '40px' }}>Loading...</div>;
    return isAuthenticated ? element : <PasswordProtection />;
  };
  
  // Main app content
  return (
    <Router>
      <ScrollToTop />
      <div className="app">
        {isAuthenticated && (
          <div className="navbar-wrapper">
            <div className="navbar-container">
              {/* Logo */}
              <Link to="/" className="navbar-logo">
                <img src="/RoboWhalesLOGO.png" alt="RoboWhales Logo" className="navbar-logo-img" />
                <h1 className="app-title">9032</h1>
              </Link>

              {/* Desktop Nav Links */}
              <nav className="navbar-links">
                <NavLink to="/" end className="nav-link">Scouting Form</NavLink>
                <NavLink to="/analysis" className="nav-link">Data Analysis</NavLink>
                <NavLink to="/admin" className="nav-link">Admin Tools</NavLink>
              </nav>

              {/* Mobile Hamburger */}
              <button
                type="button"
                className="nav-toggle"
                aria-label="Toggle navigation menu"
                aria-expanded={isNavOpen}
                onClick={() => setIsNavOpen(!isNavOpen)}
              >
                <span className="bar" />
                <span className="bar" />
                <span className="bar" />
              </button>

              {/* Mobile Dropdown */}
              {isNavOpen && (
                <div className="mobile-nav-dropdown" onClick={() => setIsNavOpen(false)}>
                  <NavLink to="/" end className="mobile-nav-link">Scouting Form</NavLink>
                  <NavLink to="/analysis" className="mobile-nav-link">Data Analysis</NavLink>
                  <NavLink to="/admin" className="mobile-nav-link">Admin Tools</NavLink>
                </div>
              )}
            </div>
          </div>
        )}

        <main>
          {isAuthenticated && <PendingSubmissionsBanner />}
          <Routes>
            <Route path="/" element={<ProtectedRoute element={<ScoutingForm />} />} />
            <Route path="/analysis" element={<ProtectedRoute element={<DataAnalysis />} />} />
            <Route path="/assistant" element={<ProtectedRoute element={<ChatBot />} />} />
            <Route path="/test-chat" element={<ProtectedRoute element={<TestChat />} />} />
            <Route path="/admin" element={<ProtectedRoute element={<AdminTools />} />} />
          </Routes>
        </main>
        
        {isAuthenticated && (
          <footer>
            <p>© {new Date().getFullYear()} RoboWhales FRC Team 9032, Cary, NC</p>
          </footer>
        )}
      </div>
    </Router>
  );
}

export default App;